import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useQuery } from '@tanstack/react-query'
import { GlobalStore } from '@highlight/ipc'
import { useGlobalStore } from '@highlight/ipc/react'
import { useFlags } from '@highlight/analytics/react'
import type { FlagReader } from '@highlight/analytics'
import { useSetChatInput } from '@renderer/chat'
import { useAuth } from '@renderer/chat/providers'
import { useUser } from '@renderer/hooks/useUser'
import { ONBOARDING_COMPLETE_FLAG } from '@shared/onboarding/onboarding-flags'
import { useViewActions } from '../../view-atoms'
import { useLiveTourStop } from './live-stop'
import {
  activeMissionAtom,
  applyTourRound,
  dayZeroTourAtom,
  findMission,
  NO_MISSION,
  requestTourRound,
  trackTour,
  unseenMissions,
  warmDayZeroTour,
  warmingRemainingMs,
  type TourStop,
} from './tour-state'

/** PostHog experiment flag; the tour shows only in its `test` variant. */
const DAY_ZERO_TOUR_FLAG = 'day-zero-tour'
const NEW_USER_WINDOW_MS = 14 * 24 * 60 * 60 * 1000
/** The gateway answers `not_ready` until the server has the onboarding stamp, which can land after the local one. */
const NOT_READY_RETRY_MS = 30_000
/** A round with only deferred missions, like a skill catalog that failed to load, asks again on this cadence. */
const DEFERRED_RETRY_MS = 5 * 60_000

export interface DayZeroTourModel {
  /** The round's stops as the app can show them right now. */
  stops: Array<TourStop>
  visited: Array<string>
  /** A later round, offering only missions added since the user finished the last one. */
  isFollowUp: boolean
  isGenerating: boolean
}

const DISABLED_TOUR: DayZeroTourModel = {
  stops: [],
  visited: [],
  isFollowUp: false,
  isGenerating: false,
}

/** Before the store syncs, the tour atom reads as empty; acting on that regenerates or overwrites a real tour. */
function useTourStoreReady(): boolean {
  const [ready, setReady] = useState(() => GlobalStore.general().isReady())
  useEffect(() => {
    if (ready) {
      return
    }
    let live = true
    void GlobalStore.general()
      .whenReady()
      .then(() => live && setReady(true))
    return () => {
      live = false
    }
  }, [ready])
  return ready
}

/** The experiment's test arm. Reading it records the exposure, so read it only for someone who would see the tour. */
export const inTourTestArm = (flags: FlagReader): boolean => flags.getValue(DAY_ZERO_TOUR_FLAG, 'control') === 'test'

/** Only new users enter the experiment, so the flag is read, and its exposure recorded, for them alone. */
export function useDayZeroTourEnabled(): boolean {
  const flags = useFlags()
  const store = useAtomValue(dayZeroTourAtom)
  const [onboardedAt] = useGlobalStore<number>('user', ONBOARDING_COMPLETE_FLAG, 0)
  // Someone already given a round stays in after the window.
  const eligible =
    (onboardedAt > 0 && Date.now() - onboardedAt < NEW_USER_WINDOW_MS) ||
    store.seen.length > 0 ||
    store.stops.length > 0
  return Boolean(store.forced) || (eligible && flags.ready && inTourTestArm(flags))
}

/** Whether another window is generating the round, re-checked when that warm-up would be presumed dead. */
function useWarming(generatingSince: number | null | undefined): boolean {
  const [, expire] = useState(0)
  useEffect(() => {
    const remaining = warmingRemainingMs(generatingSince)
    if (remaining === 0) {
      return
    }
    const timer = setTimeout(() => expire((n) => n + 1), remaining)
    return () => clearTimeout(timer)
  }, [generatingSince])
  return warmingRemainingMs(generatingSince) > 0
}

/** Generates the tour once onboarding has the context to personalize it, for users in the experiment's test arm. */
export function useWarmDayZeroTour(contextReady: boolean): void {
  const flags = useFlags()
  const { getAuthToken } = useAuth()
  useEffect(() => {
    if (contextReady && flags.ready && inTourTestArm(flags)) {
      void warmDayZeroTour(getAuthToken)
    }
  }, [contextReady, flags, getAuthToken])
}

export function useDayZeroTour(): DayZeroTourModel {
  const enabled = useDayZeroTourEnabled()
  const [store, setStore] = useAtom(dayZeroTourAtom)
  const { getAuthToken } = useAuth()
  const user = useUser()
  const ready = useTourStoreReady()
  const liveStop = useLiveTourStop()
  const unseen = useMemo(() => unseenMissions(store.seen), [store.seen])
  const warming = useWarming(store.generatingSince)
  const needsRound = enabled && ready && !warming && Boolean(user?.id) && store.stops.length === 0 && unseen.length > 0

  const query = useQuery({
    queryKey: ['day-zero-tour', user?.id, store.generation ?? 0, unseen.join(',')],
    queryFn: () => requestTourRound(getAuthToken, unseen, Boolean(store.forced)),
    enabled: needsRound,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: ({ state: { data } }) =>
      data?.status === 'not_ready'
        ? NOT_READY_RETRY_MS
        : data && data.stops.length === 0 && data.deferred.length > 0
          ? DEFERRED_RETRY_MS
          : false,
  })

  useEffect(() => {
    if (query.data) {
      setStore((prev) => applyTourRound(prev, query.data))
    }
  }, [query.data, setStore])

  const stops = useMemo(
    () =>
      store.stops.flatMap((stop) => {
        const shown = findMission(stop.mission) ? liveStop(stop) : null
        return shown ? [shown] : []
      }),
    [store.stops, liveStop],
  )

  if (!enabled) {
    return DISABLED_TOUR
  }
  const isFollowUp = (store.roundsFinished ?? 0) > 0
  return {
    stops,
    visited: store.visited.filter((id) => stops.some((stop) => stop.mission === id)),
    isFollowUp,
    // A follow-up round may hold only stops hidden for now, so it generates out of sight.
    isGenerating: !isFollowUp && (warming || (needsRound && (query.isFetching || query.data?.status === 'not_ready'))),
  }
}

export function useSettleTour() {
  const [store, setStore] = useAtom(dayZeroTourAtom)
  const setMission = useSetAtom(activeMissionAtom)
  // Unvisited stops stay unseen, like a meeting brief hidden for want of a meeting, so a later round offers them.
  const finishRound = useCallback(() => {
    trackTour('round finished', { visited: store.visited.length, stops: store.stops.length })
    setStore((prev) => ({
      ...prev,
      seen: [...new Set([...prev.seen, ...prev.visited])],
      stops: [],
      visited: [],
      roundsFinished: (prev.roundsFinished ?? 0) + 1,
    }))
  }, [store.visited.length, store.stops.length, setStore])
  // Unvisited stops count as seen too, so a dismissed round's stops are never offered again.
  const dismissRound = useCallback(() => {
    trackTour('round dismissed', { visited: store.visited.length, stops: store.stops.length })
    setMission(NO_MISSION)
    setStore((prev) => ({
      ...prev,
      seen: [...new Set([...prev.seen, ...prev.stops.map((stop) => stop.mission)])],
      stops: [],
      visited: [],
      roundsFinished: (prev.roundsFinished ?? 0) + 1,
    }))
  }, [store.visited.length, store.stops.length, setStore, setMission])
  return { finishRound, dismissRound }
}

/** Only the overlay calls this, so another window's unsynced store can't clobber the tour. */
export function useCompleteTourStop(): (mission: string, skipped: boolean) => void {
  const [store, setStore] = useAtom(dayZeroTourAtom)
  return useCallback(
    (mission: string, skipped: boolean) => {
      if (!store.stops.some((stop) => stop.mission === mission) || store.visited.includes(mission)) {
        return
      }
      trackTour(skipped ? 'mission skipped' : 'mission completed', { mission })
      setStore((prev) => ({ ...prev, visited: [...prev.visited, mission] }))
    },
    [store.stops, store.visited, setStore],
  )
}

/** Starts from Home, where every path's first click lives. */
export function useStartMission(): (stop: TourStop) => void {
  const setMission = useSetAtom(activeMissionAtom)
  const setChatInput = useSetChatInput()
  const { closeAllViews } = useViewActions()
  return useCallback(
    (stop: TourStop) => {
      closeAllViews()
      // Typing @ into a half-written message would mention the skill inside it instead of running it.
      if (stop.mission === 'run-skill') {
        setChatInput('')
      }
      trackTour('mission started', { mission: stop.mission })
      setMission({ mission: stop.mission, title: stop.title, params: stop.params ?? {}, step: 0 })
    },
    [closeAllViews, setChatInput, setMission],
  )
}
